import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import {
  getAgencyNotifications,
  getAgencyNotificationsUnreadCount,
  markAgencyNotificationRead,
  markAllAgencyNotificationsRead
} from './api';
import { NotificationsListResponse, UnreadCountResponse } from './types';

const qk = {
  list: () => ['agency_notifications'] as const,
  unread: () => ['agency_notifications_unread'] as const
};

export function useAgencyNotifications(
  params: { page?: number; per_page?: number; unread_only?: boolean } = {},
  options?: Omit<UseQueryOptions<NotificationsListResponse, Error>, 'queryKey' | 'queryFn'>
) {
  const { enabled: enabledOption, ...rest } = options ?? {};
  return useQuery<NotificationsListResponse, Error>({
    queryKey: [...qk.list(), params],
    queryFn: () => getAgencyNotifications(params),
    enabled: enabledOption ?? true,
    ...rest
  });
}

export function useAgencyNotificationsUnreadCount(
  options?: Omit<UseQueryOptions<UnreadCountResponse, Error>, 'queryKey' | 'queryFn'>
) {
  const { enabled: enabledOption, ...rest } = options ?? {};
  return useQuery<UnreadCountResponse, Error>({
    queryKey: qk.unread(),
    queryFn: () => getAgencyNotificationsUnreadCount(),
    enabled: enabledOption ?? true,
    // refetchInterval: 10_000,
    ...rest
  });
}

export function useAgencyNotificationMutations() {
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: qk.list() });
    queryClient.invalidateQueries({ queryKey: qk.unread() });
  };

  const markRead = useMutation({
    mutationFn: (id: number) => markAgencyNotificationRead(id),
    onSuccess: invalidate
  });

  const markAllRead = useMutation({
    mutationFn: () => markAllAgencyNotificationsRead(),
    onSuccess: invalidate
  });

  return { markRead, markAllRead };
}

export { qk as agencyNotificationQueryKeys };
